import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetPublicProfileQuery } from '../../redux/apis/endpoints/sharedProfileApi';
import { useCredits } from '../../hooks/useCredits';
import { useGetCreditCostQuery } from '../../redux/apis/endpoints/sharedClosetApi';
import VTOLoginConfirmationPortal from '../virtual-try-on/VTOLoginConfirmationPortal';
import CreditPurchaseModal from '../common/CreditPurchaseModal';
import { getThumbnailUrl } from '../../utils/imageUrl';
import './PersonaClosetPage.css';

const getItemImageUrl = (item) => {
  if (item.generatedProductPhoto?.url) return getThumbnailUrl(item.generatedProductPhoto.url);
  if (item.removedBgImage?.url) return getThumbnailUrl(item.removedBgImage.url);
  const originalUrl = item.image?.url || item.image;
  return getThumbnailUrl(originalUrl);
};

const PersonaClosetPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(state => state.auth);
  const { balance } = useCredits();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);

  // RTK Query for the public profile
  const { data: profileData, isLoading, isError } = useGetPublicProfileQuery(username);
  // Get cost info for the pending item
  const { data: costData, isError: costError, isLoading: costLoading } = useGetCreditCostQuery(pendingItem?.id, { skip: !pendingItem });

  const handleTryLook = (item) => {
    setPendingItem(item);
    if (!isAuthenticated) {
      setShowLoginModal(true);
    } else {
      // Defer to useEffect to check balance once costData loads
    }
  };

  useEffect(() => {
    if (isAuthenticated && pendingItem && (!costLoading)) {
      const cost = (costData && costData.cost) ? costData.cost : 2; // Default 2 credits
      if (balance < cost) {
        setShowCreditModal(true);
      } else {
        // Navigate to closet and auto-select this item for try-on
        navigate(`/my-closet?selectedClothing=${pendingItem.id}&share=${pendingItem.shareToken || ''}`);
        setPendingItem(null);
      }
    }
  }, [isAuthenticated, pendingItem, costData, costLoading, balance, navigate]);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // After login, the useEffect above will trigger and check balance
  };

  if (isLoading) {
    return (
      <div className="persona-closet-loading">
        <div className="ui active dimmer inverted">
          <div className="ui loader">Loading {username}'s closet...</div>
        </div>
      </div>
    );
  }

  if (isError || !profileData) {
    return (
      <div className="persona-closet-error">
        <h2>Closet Not Found</h2>
        <p>We couldn't find a closet for @{username}.</p>
        <button className="ui button primary" onClick={() => navigate('/')}>
          Go to Home
        </button>
      </div>
    );
  }

  const { profile, items } = profileData;

  return (
    <div className="persona-closet-page">
      {/* Mobile App Download Banner */}
      <div className="persona-mobile-banner">
        <div className="banner-content">
          <img src="/logo192.png" alt="SUSU Closet" className="banner-logo" />
          <div className="banner-text">
            <strong>SUSU Closet</strong>
            <span>Get the app for the full experience</span>
          </div>
        </div>
        <a href="https://apps.apple.com/app/id6477884218" target="_blank" rel="noopener noreferrer" className="banner-btn">
          Open App
        </a>
      </div>

      {/* Header Profile Section */}
      <div className="persona-profile-header">
        <div className="persona-avatar-wrapper">
          <img 
            src={profile.avatarUrl || '/default-avatar.png'} 
            alt={profile.username} 
            className="persona-avatar" 
          />
        </div>
        <h1 className="persona-name">{profile.displayName || profile.username}</h1>
        <div className="persona-handle">@{profile.username}</div>
        <p className="persona-bio">{profile.bio || 'Fashion enthusiast and SUSU ambassador.'}</p>
        
        <div className="persona-stats">
          <div className="stat-item">
            <strong>{items?.length || 0}</strong>
            <span>Shared Looks</span>
          </div>
        </div>
      </div>

      {/* Closet Grid Section */}
      <div className="persona-closet-grid-container">
        <h2>Shop My Closet</h2>
        {items?.length > 0 ? (
          <div className="persona-closet-grid">
            {items.map(item => (
              <div key={item.id} className="persona-item-card">
                <div className="persona-item-image-wrapper">
                  <img src={getItemImageUrl(item)} alt={item.clothesName || 'Look'} loading="lazy" />
                  <div className="try-overlay">
                    <button className="try-look-btn" onClick={() => handleTryLook(item)}>
                      Try this look
                    </button>
                  </div>
                </div>
                <div className="persona-item-details">
                  <span className="item-name">{item.clothesName || 'Shared Item'}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="persona-empty-state">
            <p>@{profile.username} hasn't shared any looks yet.</p>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <VTOLoginConfirmationPortal
        isOpen={showLoginModal}
        onLogin={handleLoginSuccess}
        onCancel={() => {
          setShowLoginModal(false);
          setPendingItem(null);
        }}
        onClose={() => {
          setShowLoginModal(false);
          setPendingItem(null);
        }}
      />

      {/* Credit Purchase / Subscription Modal */}
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => {
          setShowCreditModal(false);
          setPendingItem(null);
        }}
      />
    </div>
  );
};

export default PersonaClosetPage;
